import React, { useEffect, useState } from "react";
import { Button, DialogActions, DialogContent, DialogTitle, FormControlLabel } from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { TextLoader } from "components/apiHandling/TextLoader";
import QueryViewer from "components/queries/QueryViewer";
import DashboardSelectionButton from "components/resources/dashboards/DashboardSelectionButton";
import AlignedSwitch from "components/uiElements/inputs/AlignedSwitch";
import APIRequest from "system/APIRequest";
import { Query } from "system/query/Query";
import { QueryInfo } from "system/query/QueryInfo";
import ResponsiveDialog from "components/dialogs/ResponsiveDialog";

interface LoadDashboardDialogProps {
  onClose: () => void;
  open: boolean;
  query: Query;
}

export default function LoadDashboardDialog(props: LoadDashboardDialogProps): React.JSX.Element {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [isLoadingQueryInfo, setIsLoadingQueryInfo] = useState<boolean>(false);
  const [queryInfo, setQueryInfo] = useState<QueryInfo | null>(null);
  const [useNewTab, setUseNewTab] = useState<boolean>(false);

  async function loadQueryInfo() {
    setIsLoadingQueryInfo(true);
    try {
      const response = await APIRequest(`query-info/${props.query.id}`);
      setQueryInfo(new QueryInfo(response));
    } catch (error: any) {
      enqueueSnackbar(`${i18n.t('query-form.error-loading-query-info')}: ${error.message}`, {variant: 'error', persist: true});
      props.onClose();
    } finally {
      setIsLoadingQueryInfo(false);
    }
  }

  useEffect(() => {
    loadQueryInfo();
  }, []);

  return (
    <ResponsiveDialog
      onClose={() => props.onClose()}
      open={props.open}
    >
      <DialogTitle>{t("load-dashboard-dialog.title")}</DialogTitle>
      <DialogContent sx={{width: 600}}>
        {isLoadingQueryInfo &&
          <TextLoader />
        }
        {!isLoadingQueryInfo && queryInfo &&
          <>
            <QueryViewer query={props.query} queryInfo={queryInfo} />
            <FormControlLabel 
              control={
                <AlignedSwitch onChange={(event, checked: boolean) => setUseNewTab(checked)} value={useNewTab} />
              }
              label={t("load-dashboard-dialog.open-in-new-tab")}
              labelPlacement="start"
              sx={{marginLeft: 1}}
            />
          </>
        }
      </DialogContent>
      <DialogActions>
        <Button
          color="secondary"
          onClick={() => props.onClose()}
          variant="contained"
        >
          {t("cancel")}
        </Button>
        <DashboardSelectionButton
          datasetId={props.query.dataset_id}
          limitToTemplateDashboards={true}
          selectedDashboardId={""}
          setSelectedDashboardId={(dashboardId:string) => {
            const dashboardPath = `/dashboard/${dashboardId}/${props.query.id}`;
            if (useNewTab) {
              const origin = window.location.origin;
              window.open(`${origin}${dashboardPath}`, '_blank', 'noopener, noreferrer');
            } else {
              navigate(dashboardPath);
            }
          }}
        />
      </DialogActions>
    </ResponsiveDialog>
  )
}